/* ================================================================================
   Ancona Pass 13 — launch layer (ancona-launch.js)
   Three independently-flagged, additive features. No network writes, no PII,
   no cookies, no third-party code. Storage is versioned, capped, try/caught,
   and corrupt-JSON self-healing. Feature flags: this <script> tag's
   data-ancona-launch attribute (comma list) — remove a token to disable a
   feature; remove the script tag (layout/theme.liquid) to disable everything.
   Features: header-saved | compare
   The Pass 12 layer (ancona-innovation.js) is untouched; this file integrates
   with it only through its public CustomEvents and shared storage keys.
   ================================================================================ */
(function () {
  'use strict';
  if (window.__anconaLaunch) return;
  window.__anconaLaunch = true;

  var script = document.currentScript;
  var featAttr = script ? script.getAttribute('data-ancona-launch') : null;
  /* Absent attribute → all features; explicit empty string → none (documented revert path). */
  var FEATURES = (featAttr == null ? 'header-saved,compare' : featAttr).split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  function on(f) { return FEATURES.indexOf(f) !== -1; }

  function track(ev, payload) {
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') window.dataLayer.push(Object.assign({ event: 'ancona:' + ev }, payload || {}));
      document.dispatchEvent(new CustomEvent('ancona:' + ev, { detail: payload || {} }));
    } catch (e) { /* analytics must never break shopping */ }
  }

  var LS = null;
  try { LS = window.localStorage; LS.setItem('ancona:v1:t', '1'); LS.removeItem('ancona:v1:t'); } catch (e) { LS = null; }

  function sGet(key) {
    if (!LS) return null;
    try {
      var v = LS.getItem(key);
      return v ? JSON.parse(v) : null;
    } catch (e) {
      try { LS.removeItem(key); } catch (_) {}
      return null;
    }
  }
  function sSet(key, val) {
    if (!LS) return false;
    try { LS.setItem(key, JSON.stringify(val)); return true; } catch (e) { return false; }
  }

  /* Shared polite live region; announcements relocate inside the open dialog so
     screen readers in a modal context hear them (same pattern as Pass 12). */
  var liveRegion = null;
  var activeDialogBody = null;
  function makeLive(parent) {
    var lr = document.createElement('div');
    lr.className = 'ancona-visually-hidden';
    lr.setAttribute('aria-live', 'polite');
    lr.setAttribute('role', 'status');
    parent.appendChild(lr);
    return lr;
  }
  function announce(msg) {
    var target;
    if (activeDialogBody) {
      target = activeDialogBody.__live || (activeDialogBody.__live = makeLive(activeDialogBody));
    } else {
      target = liveRegion || (liveRegion = makeLive(document.body));
    }
    target.textContent = '';
    window.setTimeout(function () { target.textContent = msg; }, 30);
  }

  /* Accessible dialog: focus in on open, Tab loop, Escape closes, focus restored
     to opener, body scroll restored. Reuses the global .ancona-dlg styles that
     ancona-innovation.css already ships site-wide. */
  function openDialog(titleText, buildBody, opener) {
    var overlay = document.createElement('div');
    overlay.className = 'ancona-dlg__overlay';
    var dlg = document.createElement('div');
    dlg.className = 'ancona-dlg';
    dlg.setAttribute('role', 'dialog');
    dlg.setAttribute('aria-modal', 'true');
    dlg.setAttribute('aria-label', titleText);
    var head = document.createElement('div');
    head.className = 'ancona-dlg__head';
    var title = document.createElement('h2');
    title.className = 'ancona-dlg__title';
    title.textContent = titleText;
    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'ancona-dlg__close';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML = '&times;';
    head.appendChild(title); head.appendChild(close);
    var body = document.createElement('div');
    body.className = 'ancona-dlg__body';
    dlg.appendChild(head); dlg.appendChild(body);
    overlay.appendChild(dlg);

    var prevOverflow = document.body.style.overflow;
    var prevDialogBody = activeDialogBody;
    function destroy() {
      document.removeEventListener('keydown', onKey, true);
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      document.body.style.overflow = prevOverflow;
      activeDialogBody = prevDialogBody;
      /* If the opener hid itself while the dialog was open (hub chips self-hide
         at count 0), focus() on it would be a no-op — land on a stable header
         control instead of dropping the user to <body>. */
      var openerUsable = opener && opener.focus && !opener.hidden;
      if (openerUsable) opener.focus();
      if (!openerUsable || document.activeElement !== opener) {
        var fb = document.getElementById('cart-icon-bubble') || document.querySelector('.header__icons a, .header__icons button');
        if (fb && fb.focus) fb.focus();
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); destroy(); return; }
      if (e.key === 'Tab') {
        var f = dlg.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]');
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        /* If focus escaped to a non-interactive spot (e.g. a click on dialog
           padding), pull it back inside instead of letting Tab reach the page. */
        if (!dlg.contains(document.activeElement)) { e.preventDefault(); first.focus(); return; }
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    overlay.addEventListener('click', function (e) { if (e.target === overlay) destroy(); });
    close.addEventListener('click', destroy);
    document.addEventListener('keydown', onKey, true);
    activeDialogBody = body;
    buildBody(body, destroy);
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    close.focus();
    return { close: destroy, root: dlg, body: body };
  }

  /* ---- Storage lists ---------------------------------------------------------- */
  var SAVED_KEY = 'ancona:v1:saved';     /* owned by Pass 12; read + written with the same shape */
  var COMPARE_KEY = 'ancona:v1:compare'; /* owned by this layer; cap 3 */

  function listOf(key) {
    var v = sGet(key);
    return Array.isArray(v) ? v.filter(function (x) { return x && typeof x.h === 'string' && typeof x.u === 'string'; }) : [];
  }
  function savedList() { return listOf(SAVED_KEY); }
  function compareList() { return listOf(COMPARE_KEY); }
  function compareWrite(list) { return sSet(COMPARE_KEY, list.slice(0, 3)); }
  function compareHas(handle) { return compareList().some(function (x) { return x.h === handle; }); }

  function itemRow(item, removeLabel, onRemove) {
    var li = document.createElement('li');
    li.className = 'ancona-dlg__item';
    var a = document.createElement('a');
    a.href = item.u;
    if (item.i) {
      var img = document.createElement('img');
      img.src = item.i; img.alt = ''; img.loading = 'lazy'; img.width = 64; img.height = 64;
      a.appendChild(img);
    }
    var meta = document.createElement('span');
    meta.className = 'ancona-dlg__meta';
    var t = document.createElement('span'); t.className = 'ancona-dlg__t'; t.textContent = item.t || item.h;
    meta.appendChild(t);
    if (item.p) { var p = document.createElement('span'); p.className = 'ancona-dlg__p'; p.textContent = item.p; meta.appendChild(p); }
    a.appendChild(meta);
    li.appendChild(a);
    if (onRemove) {
      var rm = document.createElement('button');
      rm.type = 'button'; rm.className = 'ancona-dlg__rm';
      rm.setAttribute('aria-label', 'Remove ' + (item.t || item.h) + ' from ' + removeLabel);
      rm.textContent = 'Remove';
      rm.addEventListener('click', function () { onRemove(item, li); });
      li.appendChild(rm);
    }
    return li;
  }

  /* ---- 1) HEADER HUB (saved access + compare access) --------------------------- */
  var hubSavedBtn = null, hubCompareBtn = null;

  function ensureHub() {
    var icons = document.querySelector('.header__icons');
    if (!icons) return null;
    var hub = document.getElementById('AnconaHub');
    if (!hub) {
      hub = document.createElement('div');
      hub.id = 'AnconaHub';
      hub.className = 'ancona-hub';
      var anchor = icons.querySelector('a[href*="/account"], a[href*="customer_authentication"]') || icons.querySelector('#cart-icon-bubble');
      icons.insertBefore(hub, anchor || null);
    }
    return hub;
  }

  function hubButton(cls, iconPath, onClick) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'ancona-hub__btn ' + cls;
    b.hidden = true;
    b.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true" focusable="false">' + iconPath + '</svg><span class="ancona-hub__count" aria-hidden="true"></span>';
    b.addEventListener('click', onClick);
    return b;
  }

  function refreshHub() {
    if (hubSavedBtn) {
      var n = savedList().length;
      hubSavedBtn.hidden = n === 0;
      hubSavedBtn.querySelector('.ancona-hub__count').textContent = String(n);
      hubSavedBtn.setAttribute('aria-label', 'Saved pieces (' + n + '), saved in this browser');
    }
    if (hubCompareBtn) {
      var c = compareList().length;
      hubCompareBtn.hidden = c === 0;
      hubCompareBtn.querySelector('.ancona-hub__count').textContent = String(c);
      hubCompareBtn.setAttribute('aria-label', 'Compare pieces (' + c + '), saved in this browser');
    }
  }

  function buildHubDom() {
    var hub = ensureHub();
    if (!hub) return false;
    if (on('header-saved') && !hubSavedBtn) {
      hubSavedBtn = hubButton('ancona-hub__saved',
        '<path d="M12 21s-7.5-4.7-10-9.3C.5 8 2.6 4.5 6.2 4.5c2.2 0 3.9 1.2 4.8 3 0.9-1.8 2.6-3 4.8-3 3.6 0 5.7 3.5 4.2 7.2C19.5 16.3 12 21 12 21z"/>',
        function () { track('saved_open', { from: 'header' }); openSavedHub(hubSavedBtn); });
      hub.appendChild(hubSavedBtn);
    }
    if (on('compare') && !hubCompareBtn) {
      hubCompareBtn = hubButton('ancona-hub__compare',
        '<path d="M9 3v18M15 3v18M3 8h6M15 8h6M4.5 8l-2 5h5l-2-5zM19.5 8l-2 5h5l-2-5z"/>',
        function () { track('compare_open', { from: 'header' }); openCompareDialog(hubCompareBtn); });
      hub.appendChild(hubCompareBtn);
    }
    refreshHub();
    return true;
  }

  function initHub() {
    if (!LS) return;
    if (!buildHubDom()) return;
    /* Stay fresh: Pass 12 dispatches these on PDP save/remove … */
    document.addEventListener('ancona:saved_add', refreshHub);
    document.addEventListener('ancona:saved_remove', refreshHub);
    /* … and its dialog's Remove/Clear buttons are covered by one delegated
       listener, so the Pass 12 file needs no patch at all. */
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (t && t.closest && (t.closest('.ancona-dlg__rm') || t.closest('.ancona-dlg__clear'))) {
        window.setTimeout(refreshHub, 80);
      }
    });
  }

  /* Keep the Pass 12 PDP save button honest when this layer's dialog mutates the
     shared saved list. handle === null means "cleared" (reset regardless). */
  function syncPdpSaveBtn(handle) {
    var saveBtn = document.getElementById('AnconaSaveBtn');
    if (!saveBtn) return;
    if (handle !== null && saveBtn.dataset.handle !== handle) return;
    saveBtn.setAttribute('aria-pressed', 'false');
    saveBtn.classList.remove('is-saved');
    var label = saveBtn.querySelector('.ancona-save__label');
    if (label) label.textContent = 'Save this piece';
  }

  function openSavedHub(opener) {
    openDialog('Your saved pieces', function (body) {
      var note = document.createElement('p');
      note.className = 'ancona-dlg__note';
      note.textContent = 'Saved in this browser.';
      body.appendChild(note);
      var emptyNote = document.createElement('p');
      emptyNote.className = 'ancona-dlg__note';
      emptyNote.textContent = 'Nothing saved yet.';
      emptyNote.hidden = true;
      var ul = document.createElement('ul');
      ul.className = 'ancona-dlg__list';
      ul.setAttribute('role', 'list');
      body.appendChild(emptyNote);
      body.appendChild(ul);

      function paint() {
        ul.textContent = '';
        var list = savedList();
        emptyNote.hidden = list.length > 0;
        list.forEach(function (item) {
          ul.appendChild(itemRow(item, 'saved pieces', function (it, li) {
            sSet(SAVED_KEY, savedList().filter(function (x) { return x.h !== it.h; }).slice(0, 24));
            var sibling = li.nextElementSibling || li.previousElementSibling;
            var nextFocus = (sibling && sibling.querySelector('.ancona-dlg__rm')) || body.querySelector('.ancona-dlg__clear');
            li.remove();
            if (nextFocus && nextFocus.focus) nextFocus.focus();
            announce('Removed from saved pieces.');
            track('saved_remove', { handle: it.h });
            refreshHub();
            syncPdpSaveBtn(it.h);
            if (!savedList().length) emptyNote.hidden = false;
          }));
        });
      }
      paint();

      var actions = document.createElement('div');
      actions.className = 'ancona-dlg__actions';
      var clear = document.createElement('button');
      clear.type = 'button'; clear.className = 'ancona-dlg__clear';
      clear.textContent = 'Clear saved pieces';
      clear.addEventListener('click', function () {
        if (!window.confirm('Clear all saved pieces in this browser?')) return;
        sSet(SAVED_KEY, []); paint(); announce('Saved pieces cleared.'); refreshHub();
        syncPdpSaveBtn(null);
      });
      actions.appendChild(clear);
      body.appendChild(actions);
    }, opener);
  }

  /* ---- 2) PRODUCT COMPARE (PDP toggle + dialog, cap 3, browser-local) ---------- */
  function reflectCompareBtn(btn) {
    var inList = compareHas(btn.dataset.handle);
    btn.setAttribute('aria-pressed', inList ? 'true' : 'false');
    btn.querySelector('.ancona-cmpbtn__label').textContent = inList ? 'In compare' : 'Compare this piece';
    btn.classList.toggle('is-active', inList);
  }

  function wireCompareBtn() {
    if (!LS) return;
    var wrap = document.querySelector('.ancona-cmpwrap');
    var btn = document.getElementById('AnconaCompareBtn');
    /* Ships hidden; revealed only when storage works and the feature is on, so a
       disabled feature / denied storage / JS failure never leaves a dead button.
       data-ancona-wired guards against double listeners on editor re-renders. */
    if (btn && !btn.dataset.anconaWired) {
      btn.dataset.anconaWired = '1';
      if (wrap) wrap.hidden = false;
      btn.addEventListener('click', function () {
        var d = btn.dataset;
        var list = compareList();
        if (compareHas(d.handle)) {
          compareWrite(list.filter(function (x) { return x.h !== d.handle; }));
          announce('Removed from compare.');
          track('compare_remove', { handle: d.handle });
        } else if (list.length >= 3) {
          announce('Compare holds up to 3 pieces. Remove one to add another.');
        } else {
          list.push({ h: d.handle, u: d.url, t: (d.title || '').slice(0, 80), p: (d.price || '').slice(0, 24), i: d.image || '', y: (d.type || '').slice(0, 40), ts: Date.now() });
          if (!compareWrite(list)) { announce('Compare is unavailable in this browser.'); return; }
          announce('Added to compare. Saved in this browser.');
          track('compare_add', { handle: d.handle });
        }
        reflectCompareBtn(btn);
        refreshHub();
      });
      reflectCompareBtn(btn);
    }
  }
  function initCompare() { wireCompareBtn(); }

  function compareCol(item, onRemove) {
    var col = document.createElement('div');
    col.className = 'ancona-cmp__col';
    /* Decorative duplicate of the title link below — remove it from the tab
       order and the accessibility tree (no empty/unnamed tab stop). */
    var a = document.createElement('a');
    a.href = item.u;
    a.className = 'ancona-cmp__media';
    a.tabIndex = -1;
    a.setAttribute('aria-hidden', 'true');
    if (item.i) {
      var img = document.createElement('img');
      img.src = item.i; img.alt = ''; img.loading = 'lazy'; img.width = 96; img.height = 96;
      a.appendChild(img);
    }
    col.appendChild(a);
    var t = document.createElement('a');
    t.href = item.u; t.className = 'ancona-cmp__t'; t.textContent = item.t || item.h;
    col.appendChild(t);
    if (item.p) { var p = document.createElement('p'); p.className = 'ancona-cmp__p'; p.textContent = item.p; col.appendChild(p); }
    if (item.y) { var y = document.createElement('p'); y.className = 'ancona-cmp__y'; y.textContent = item.y; col.appendChild(y); }
    var rm = document.createElement('button');
    rm.type = 'button'; rm.className = 'ancona-dlg__rm';
    rm.setAttribute('aria-label', 'Remove ' + (item.t || item.h) + ' from compare');
    rm.textContent = 'Remove';
    rm.addEventListener('click', function () { onRemove(item, col); });
    col.appendChild(rm);
    return col;
  }

  function openCompareDialog(opener) {
    openDialog('Compare pieces', function (body) {
      var note = document.createElement('p');
      note.className = 'ancona-dlg__note';
      note.textContent = 'Saved in this browser.';
      body.appendChild(note);
      var hint = document.createElement('p');
      hint.className = 'ancona-dlg__note ancona-cmp__hint';
      var grid = document.createElement('div');
      grid.className = 'ancona-cmp';
      body.appendChild(hint);
      body.appendChild(grid);

      function paint() {
        grid.textContent = '';
        var list = compareList();
        grid.setAttribute('data-count', String(list.length));
        hint.hidden = list.length >= 2;
        hint.textContent = list.length === 0 ? 'Nothing to compare yet.' : 'Choose at least two pieces to compare.';
        list.forEach(function (item) {
          grid.appendChild(compareCol(item, function (it, col) {
            compareWrite(compareList().filter(function (x) { return x.h !== it.h; }));
            /* In-place removal (no repaint) so the focus target we pick survives. */
            var sibling = col.nextElementSibling || col.previousElementSibling;
            var nextFocus = (sibling && sibling.querySelector('.ancona-dlg__rm')) || body.querySelector('.ancona-dlg__clear');
            col.remove();
            var n = compareList().length;
            grid.setAttribute('data-count', String(n));
            hint.hidden = n >= 2;
            hint.textContent = n === 0 ? 'Nothing to compare yet.' : 'Choose at least two pieces to compare.';
            if (nextFocus && nextFocus.focus) nextFocus.focus();
            announce('Removed from compare.');
            track('compare_remove', { handle: it.h });
            var pdpBtn = document.getElementById('AnconaCompareBtn');
            if (pdpBtn && pdpBtn.dataset.handle === it.h) reflectCompareBtn(pdpBtn);
            refreshHub();
          }));
        });
      }
      paint();

      var actions = document.createElement('div');
      actions.className = 'ancona-dlg__actions';
      var clear = document.createElement('button');
      clear.type = 'button'; clear.className = 'ancona-dlg__clear';
      clear.textContent = 'Clear compare';
      clear.addEventListener('click', function () {
        compareWrite([]); paint(); announce('Compare list cleared.');
        var pdpBtn = document.getElementById('AnconaCompareBtn');
        if (pdpBtn) reflectCompareBtn(pdpBtn);
        refreshHub();
      });
      actions.appendChild(clear);
      body.appendChild(actions);
    }, opener);
  }

  function boot() {
    /* Each feature is isolated: a throw in one can never abort the others.
       initHub hosts both header chips; it runs when either chip feature is on. */
    if (on('header-saved') || on('compare')) {
      try { initHub(); } catch (e) { /* one feature failing must not break the page */ }
    }
    if (on('compare')) {
      try { initCompare(); } catch (e) { /* one feature failing must not break the page */ }
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Theme editor only: section re-renders replace the header / product DOM,
     destroying the injected hub and the compare button's listener. Rebuild
     the DOM pieces; document-level listeners are bound once and survive. */
  document.addEventListener('shopify:section:load', function () {
    try {
      if ((on('header-saved') || on('compare')) && !document.getElementById('AnconaHub')) {
        hubSavedBtn = null; hubCompareBtn = null;
        buildHubDom();
      }
      if (on('compare')) wireCompareBtn();
    } catch (e) { /* editor resilience must never break the page */ }
  });
})();
